import '.ab{position:relative;}.ssr-plugin'
import '.n{width:100%;}.ssr-plugin'
import '.ak{border-radius:1.25rem;}.ssr-plugin'
import '.b5{overflow:hidden;}.ssr-plugin'
import '.bx{position:absolute;}.ssr-plugin'
import '.jh{inset:0px;}.ssr-plugin'
import '.gy{opacity:0;}.ssr-plugin'
import '.ji{transition:opacity 0.2s ease;}.ssr-plugin'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { Alert } from '@app/components/core/alert/Alert'
import { FileSelector } from '@app/components/core/input/text/FileSelector'
import { Image } from '@app/components/core/image/Image'
import {
  type ExistingImage,
  type ImagePreview,
} from '@app/components/blocks/imageEditRow/ImageEditRow'
import { PreviewImage } from '@app/components/core/image/PreviewImage'
import { faImage, faPlus, faUpload } from '@fortawesome/pro-regular-svg-icons'
import { Dispatch, useCallback, useMemo, useState } from 'react'
import { styled, type Style } from 'inlines'
import { CloseButton } from '@app/components/core/button/CloseButton'

const MAX_IMAGE_SIZE_MB = 0.5

export type InitiativeImageEditorProps = {
  existingImages: ExistingImage[]
  newImages: ImagePreview[]
  removedImages: number[]
  infoMessage: string
  bannerLabel: string
  uploadBannerLabel: string
  maxImages?: number
  extraPlaceholdersDisplayed?: number
  setNewImages: Dispatch<React.SetStateAction<ImagePreview[]>>
  setRemovedImages: Dispatch<React.SetStateAction<number[]>>
}

export const InitiativeImageEditor = ({
  existingImages,
  newImages,
  removedImages,
  infoMessage,
  bannerLabel,
  uploadBannerLabel,
  maxImages = 10,
  extraPlaceholdersDisplayed = 2,
  setNewImages,
  setRemovedImages,
}: InitiativeImageEditorProps) => {
  const [allowedType, setAllowedType] = useState(true)

  const visibleExisting = useMemo(
    () => existingImages.filter((image) => !removedImages.includes(image.id)),
    [existingImages, removedImages],
  )

  const imageCount = visibleExisting.length + newImages.length
  const imagesToAdd = maxImages - imageCount

  const bannerNew = newImages[0]
  const bannerExisting = bannerNew ? undefined : visibleExisting[0]
  const bannerSrc = bannerNew?.preview ?? bannerExisting?.src ?? null

  const galleryExisting = bannerNew ? visibleExisting : visibleExisting.slice(1)
  const galleryNew = newImages[0] ? newImages.slice(1) : newImages

  const galleryCount = galleryExisting.length + galleryNew.length
  const placeholdersToRender = Math.max(
    extraPlaceholdersDisplayed - galleryCount,
    0,
  )

  const addGalleryImages = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return
      }
      setNewImages((prev) => [
        ...prev,
        ...files.map((file) => ({
          preview: URL.createObjectURL(file),
          file,
        })),
      ])
    },
    [setNewImages],
  )

  const handleDisallowedFileType = useCallback(() => {
    setAllowedType(false)
  }, [])

  const handleGalleryUpload = useCallback(
    (files: File[]) => {
      if (files.length > 0) {
        setAllowedType(true)
      }
      addGalleryImages(files)
    },
    [addGalleryImages],
  )

  const replaceBanner = useCallback(
    (files: File[]) => {
      const file = files[0]
      if (!file) {
        return
      }

      if (newImages[0]) {
        URL.revokeObjectURL(newImages[0].preview)
        setNewImages((prev) => [
          { preview: URL.createObjectURL(file), file },
          ...prev.slice(1),
        ])
        return
      }

      if (visibleExisting[0]) {
        setRemovedImages((prev) => [...prev, visibleExisting[0].id])
      }

      setNewImages((prev) => [
        { preview: URL.createObjectURL(file), file },
        ...prev,
      ])
    },
    [newImages, setNewImages, setRemovedImages, visibleExisting],
  )

  const handleBannerUpload = useCallback(
    (files: File[]) => {
      if (files.length > 0) {
        setAllowedType(true)
      }
      replaceBanner(files)
    },
    [replaceBanner],
  )

  const removeBanner = useCallback(() => {
    if (newImages[0]) {
      URL.revokeObjectURL(newImages[0].preview)
      setNewImages((prev) => prev.slice(1))
      return
    }

    if (visibleExisting[0]) {
      setRemovedImages((prev) => [...prev, visibleExisting[0].id])
    }
  }, [newImages, setNewImages, setRemovedImages, visibleExisting])

  const removeGalleryExisting = useCallback(
    (id: number) => {
      setRemovedImages((prev) => [...prev, id])
    },
    [setRemovedImages],
  )

  const cancelGalleryExistingDelete = useCallback(
    (id: number) => {
      setRemovedImages((prev) => prev.filter((imageId) => imageId !== id))
    },
    [setRemovedImages],
  )

  const removeGalleryNew = useCallback(
    (preview: string) => {
      setNewImages((prev) => {
        const next = prev.filter((image) => image.preview !== preview)
        if (prev.some((image) => image.preview === preview)) {
          URL.revokeObjectURL(preview)
        }
        return next
      })
    },
    [setNewImages],
  )

  return (
    <Column gap="1rem" alignItems="stretch">
      <BannerSlot
        src={bannerSrc}
        isUploadPreview={!!bannerNew}
        bannerLabel={bannerLabel}
        uploadBannerLabel={uploadBannerLabel}
        canUpload={imagesToAdd > 0}
        onUpload={handleBannerUpload}
        onDisallowedFileTypeSelected={handleDisallowedFileType}
        onRemove={bannerSrc ? removeBanner : undefined}
      />

      <Row style={{ flexWrap: 'wrap' }}>
        {galleryExisting.map((image) => (
          <PreviewImage
            key={`existing_${image.id}`}
            src={image.src}
            size="large"
            onDelete={() => removeGalleryExisting(image.id)}
            onCancelDelete={
              removedImages.includes(image.id)
                ? () => cancelGalleryExistingDelete(image.id)
                : null
            }
          />
        ))}
        {galleryNew.map((image, index) => (
          <PreviewImage
            key={`new_${index}`}
            src={image.preview}
            size="large"
            onDelete={() => removeGalleryNew(image.preview)}
            isUploadPreview={true}
          />
        ))}

        {placeholdersToRender > 0 &&
          Array.from({ length: placeholdersToRender }).map((_, index) => (
            <FileSelector
              key={`placeholder_${index}`}
              icon={faImage}
              allowedFileTypes={[
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/webp',
              ]}
              maxFiles={imagesToAdd}
              maxSizeMB={MAX_IMAGE_SIZE_MB}
              iconStyle={placeholderIconStyle}
              containerStyle={thumbnailSelectorStyle}
              onFileSelectionUpdated={handleGalleryUpload}
              onDisallowedFileTypeSelected={handleDisallowedFileType}
              disabled={imagesToAdd <= 0}
            />
          ))}

        {imagesToAdd > 0 && (
          <FileSelector
            icon={faPlus}
            allowedFileTypes={[
              'image/jpeg',
              'image/jpg',
              'image/png',
              'image/webp',
            ]}
            maxFiles={imagesToAdd}
            maxSizeMB={MAX_IMAGE_SIZE_MB}
            iconStyle={placeholderIconStyle}
            containerStyle={thumbnailSelectorStyle}
            onFileSelectionUpdated={handleGalleryUpload}
            onDisallowedFileTypeSelected={handleDisallowedFileType}
          />
        )}
      </Row>

      <Alert
        variant={allowedType ? 'info' : 'error'}
        size="small"
        message={infoMessage}
      />
    </Column>
  )
}

const BannerSlot = ({
  src,
  isUploadPreview = false,
  bannerLabel,
  uploadBannerLabel,
  canUpload,
  onUpload,
  onDisallowedFileTypeSelected,
  onRemove,
}: {
  src: string | null
  isUploadPreview?: boolean
  bannerLabel: string
  uploadBannerLabel: string
  canUpload: boolean
  onUpload: (files: File[]) => void
  onDisallowedFileTypeSelected: () => void
  onRemove?: () => void
}) => {
  if (!src) {
    return (
      <FileSelector
        icon={faImage}
        label={bannerLabel}
        maxFiles={1}
        allowedFileTypes={[
          'image/jpeg',
          'image/jpg',
          'image/png',
          'image/webp',
        ]}
        maxSizeMB={MAX_IMAGE_SIZE_MB}
        iconStyle={placeholderIconStyle}
        labelStyle={{ color: 'var(--palette-blue-400)' }}
        containerStyle={bannerPlaceholderStyle}
        onFileSelectionUpdated={onUpload}
        onDisallowedFileTypeSelected={onDisallowedFileTypeSelected}
        disabled={!canUpload}
      />
    )
  }

  return (
    <div className="ab n"
    >
      <styled.div className="ab ak b5"
        style={{
          '&:hover .banner-replace-overlay': {
            opacity: 1,
          },
        }}
      >
        <Image
          src={src}
          ignoreResize={isUploadPreview}
          resizeWidth={800}
          style={{
            display: 'block',
            width: '100%',
            height: '16rem',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
        {canUpload && (
          <div className="banner-replace-overlay bx jh gy ji"
          >
            <FileSelector
              icon={faUpload}
              label={uploadBannerLabel}
              maxFiles={1}
              allowedFileTypes={[
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/webp',
              ]}
              maxSizeMB={MAX_IMAGE_SIZE_MB}
              iconStyle={{
                width: '3rem',
                height: '3rem',
                color: 'var(--palette-white)',
              }}
              labelStyle={{ color: 'var(--palette-white)' }}
              containerStyle={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(19, 15, 51, 0.55)',
                cursor: 'pointer',
              }}
              onFileSelectionUpdated={onUpload}
              onDisallowedFileTypeSelected={onDisallowedFileTypeSelected}
            />
          </div>
        )}
      </styled.div>
      {onRemove && (
        <CloseButton
          onClick={onRemove}
          style={{
            background: 'var(--brand-primary)',
            color: 'var(--palette-white)',
            position: 'absolute',
            top: '0.75rem',
            right: '0.75rem',
            transform: 'translate(50%, -50%)',
            zIndex: 2,
            width: '2.5rem',
            height: '2.5rem',
            padding: 0,
            boxSizing: 'border-box',
            border: '0.125rem solid var(--palette-white)',
          }}
        />
      )}
    </div>
  )
}

const bannerPlaceholderStyle: Style = {
  height: '16rem',
  borderRadius: '1.25rem',
  background: 'var(--gray-100)',
  justifyContent: 'center',
  cursor: 'pointer',
}

const placeholderIconStyle: Style = {
  width: '3rem',
  height: '3rem',
  color: 'var(--palette-blue-400)',
}

const thumbnailSelectorStyle: Style = {
  width: '9.325rem',
  height: '9.325rem',
  borderRadius: '1.25rem',
  background: 'var(--gray-100)',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}
